import { useEffect } from "react";
import { supabase } from "../lib/supabase";
import { useToast } from "../lib/toast";

// RadioRewards — listens for `radio:ad-reward` events fired by
// BackgroundMusic when an ad with a reward payload plays through
// audibly + tab visible. Owns the per-user, per-day cap (localStorage
// keyed on userId + reward.code) and the actual XP grant via the
// award_xp RPC. Surfaces a toast so the user knows the bonus dropped.
//
// Mounted once at the App level. Returns null — pure side-effect
// component.
//
// Why a separate component instead of bundling into BackgroundMusic:
// BackgroundMusic doesn't have user-id context (it's mounted before
// auth is settled in some flows). Splitting concerns lets the audio
// machinery stay auth-agnostic.
//
// Anonymous / signed-out users get no reward — there's no user to
// credit. The toast doesn't fire either, so the absence of grant
// doesn't surface as a broken promise.

const CLAIM_KEY = (userId, code, day) => `radio:ad-claim:${userId}:${code}:${day}`;

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function RadioRewards({ userId }) {
  const { push } = useToast() || {};

  useEffect(() => {
    if (!userId) return undefined;

    const onReward = async (e) => {
      const reward = e?.detail?.reward;
      if (!reward?.code) return;

      // Per-user-per-day localStorage cap. Defends in depth alongside
      // the xp_source_values.per_day_cap=1 server-side guard.
      const day = todayLocal();
      const key = CLAIM_KEY(userId, reward.code, day);
      try {
        if (typeof localStorage !== "undefined" && localStorage.getItem(key)) {
          return; // already claimed today
        }
      } catch { /* ignore */ }

      // Only XP rewards in v1. Future kinds (free roll, double-XP
      // token, rare item) get their own dispatch branch here.
      if (reward.kind === "xp") {
        try {
          const { error } = await supabase.rpc("award_xp", {
            p_user_id:   userId,
            p_source:    "radio_ad_bonus",
            p_ref_table: null,
            p_ref_id:    null,
          });
          if (error) {
            // per_day_cap may have already been hit on the server
            // side (different device, refresh, etc.) — silent fail
            // is correct, no toast for a non-grant.
            // eslint-disable-next-line no-console
            console.warn("[radio-reward] award_xp failed:", error.message);
            return;
          }
          try { localStorage.setItem(key, new Date().toISOString()); } catch { /* ignore */ }

          const label = reward.label || "Radio bonus";
          if (push) {
            push(`${label} — bonus XP just landed`, { emoji: "🎁", kind: "info", ttl: 5500 });
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[radio-reward] grant threw:", err);
        }
      }
    };

    window.addEventListener("radio:ad-reward", onReward);
    return () => window.removeEventListener("radio:ad-reward", onReward);
  }, [userId, push]);

  return null;
}
